import React, { useState, useMemo } from 'react';
import {
    Autocomplete,
    TextField,
    Checkbox,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import { Home } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '../../utils/debounce';
import api from '../../api/axios';

interface VillageOption {
    id: number;
    name: string;
    pincode?: string;
}

interface VillageAutocompleteProps {
    /** Selected values - array for multiple, single object for single select */
    value: VillageOption[] | VillageOption | null;
    /** Change handler */
    onChange: (value: any) => void;
    /** Allow multiple selection */
    multiple?: boolean;
    /** Filter by constituency IDs */
    constituencyIds?: number[];
    /** Autocomplete size */
    size?: 'small' | 'medium';
    /** Input label */
    label?: string;
    /** Input placeholder */
    placeholder?: string;
    /** Whether the component is disabled */
    disabled?: boolean;
    /** Error state */
    error?: boolean;
    /** Show home icon */
    showIcon?: boolean;
    /** Max tags to show in multi-select before +N */
    limitTags?: number;
    /** Only fetch when enabled (e.g. dialog is open) */
    enabled?: boolean;
    /** Grid size — full width by default */
    fullWidth?: boolean;
}

const VillageAutocomplete: React.FC<VillageAutocompleteProps> = ({
    value,
    onChange,
    multiple = false,
    constituencyIds,
    size = 'small',
    label = 'Villages',
    placeholder,
    disabled = false,
    error = false,
    showIcon = true,
    limitTags = 2,
    enabled = true,
    fullWidth = true,
}) => {
    const [inputValue, setInputValue] = useState('');
    const debouncedInput = useDebounce(inputValue, 300);

    const { data: options = [], isLoading } = useQuery({
        queryKey: ['villages-autocomplete', debouncedInput, constituencyIds],
        queryFn: async () => {
            const params: any = { per_page: 500 };
            if (debouncedInput) params.search = debouncedInput;
            if (constituencyIds && constituencyIds.length > 0) {
                params['constituency_id[]'] = constituencyIds;
            }
            const res = await api.get('/tenant/villages', { params });
            return res.data?.data?.data || res.data?.data || [];
        },
        enabled,
    });

    // Merge selected values with fetched options to ensure selected items always appear
    const mergedOptions = useMemo(() => {
        const selectedArr = multiple
            ? (value as VillageOption[] || [])
            : (value ? [value as VillageOption] : []);
        const optionIds = new Set(options.map((o: VillageOption) => o.id));
        const missing = selectedArr.filter((v) => !optionIds.has(v.id));
        return [...missing, ...options];
    }, [options, value, multiple]);

    const defaultPlaceholder = multiple
        ? (Array.isArray(value) && value.length === 0 ? 'All' : '')
        : 'Select village';

    return (
        <Autocomplete
            multiple={multiple as any}
            size={size}
            options={mergedOptions}
            getOptionLabel={(option: VillageOption) => option.name || ''}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            value={value as any}
            onChange={(_, newValue) => onChange(newValue)}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
            loading={isLoading}
            filterOptions={(x) => x}
            limitTags={limitTags}
            disableCloseOnSelect={multiple}
            disabled={disabled}
            fullWidth={fullWidth}
            renderOption={(props, option, { selected }) =>
                multiple ? (
                    <li {...props}>
                        <Checkbox size="small" style={{ marginRight: 8 }} checked={selected} />
                        {option.name}
                    </li>
                ) : (
                    <li {...props}>{option.name}</li>
                )
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder || defaultPlaceholder}
                    error={error}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                {showIcon && (
                                    <InputAdornment position="start">
                                        <Home fontSize="small" color="action" />
                                    </InputAdornment>
                                )}
                                {params.InputProps.startAdornment}
                            </>
                        ),
                        endAdornment: (
                            <>
                                {isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export default VillageAutocomplete;
